import { useState, useEffect, useCallback } from 'react';
import { sessionService } from '../../services/api';
import tenantStorage from '../../services/tenantStorage';

const useSessionManager = ({ employeeId, eodMode, setIsBusinessDay, setBusinessDaySalesDate, logout }) => {
    const [activeSession, setActiveSession] = useState(null);
    const [showSoD, setShowSoD] = useState(false);
    const [showEoD, setShowEoD] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(true);

    // User-QC finding D: extracted so it can be called both on mount/employee
    // change (below) AND on-demand by callers that need a fresh read instead
    // of the cached `activeSession` — see `refreshSession` in the returned
    // object. Previously the "no session" branch only flipped `showSoD` and
    // left the stale `activeSession` object in place; since callers gate on
    // `showSoD && !activeSession` (SellSurface.js), a stale truthy
    // `activeSession` silently defeated that gate and resumed a session the
    // backend had actually already closed. Explicitly nulling it here is
    // part of the fix, independent of when/why the recheck runs.
    const checkSession = useCallback(async () => {
        try {
            const sessions = await sessionService.getActiveSessions();
            const currentEmployeeId = employeeId?.startsWith('ACC_') ? employeeId : employeeId;

            const mySession = eodMode === 'per_store'
                ? (sessions.length > 0 ? sessions[0] : null)
                : sessions.find(s => s.employee_id === currentEmployeeId);

            if (mySession) {
                setActiveSession(mySession);
                setShowSoD(false);
                setShowEoD(false);
                const sessionDate = mySession.sales_date || new Date().toISOString().split('T')[0];
                setIsBusinessDay(true);
                setBusinessDaySalesDate(sessionDate);
                try {
                    await tenantStorage.setState('isBusinessDay', true);
                    await tenantStorage.setState('salesDate', sessionDate);
                } catch (e) { console.error('Failed to persist business day state:', e); }
            } else {
                setActiveSession(null);
                setShowSoD(true);
                setShowEoD(false);
            }
        } catch (err) {
            console.error('Failed to check session:', err);
            setShowSoD(true);
        } finally {
            setSessionLoading(false);
        }
    }, [employeeId, eodMode, setIsBusinessDay, setBusinessDaySalesDate]);

    useEffect(() => {
        if (employeeId) checkSession();
        else setSessionLoading(false);
        // eodMode is included so a mid-session EOD-mode change re-checks the
        // session against the correct (per_store vs per-employee) lookup
        // rather than reusing a stale closure.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, eodMode]);

    // Additive: an on-demand re-check for callers that need to invalidate the
    // cached `activeSession` outside the employeeId-change effect above —
    // e.g. the native surface re-validating on every operator unlock, even a
    // repeat unlock by the SAME employee, where `employeeId` never changes
    // and the effect above would never re-fire on its own.
    const refreshSession = useCallback(() => {
        if (!employeeId) return Promise.resolve();
        setSessionLoading(true);
        return checkSession();
    }, [employeeId, checkSession]);

    const handleSessionStarted = async (session) => {
        setActiveSession(session);
        setShowSoD(false);
        const sessionDate = session.sales_date || new Date().toISOString().split('T')[0];
        setIsBusinessDay(true);
        setBusinessDaySalesDate(sessionDate);
        try {
            await tenantStorage.setState('isBusinessDay', true);
            await tenantStorage.setState('salesDate', sessionDate);
        } catch (e) { console.error('Failed to persist business day state:', e); }
    };

    const handleEndSession = () => setShowEoD(true);

    const handleSessionClosed = () => logout();

    return {
        activeSession,
        showSoD, setShowSoD,
        showEoD, setShowEoD,
        sessionLoading,
        handleSessionStarted,
        handleEndSession,
        handleSessionClosed,
        refreshSession,
    };
};

export default useSessionManager;
